// @ts-check
/** Repeatable list of payment lines for a partial/split payment - each with an amount, a
 * method (cash/UPI/bank transfer - never a pending gateway link), and an optional reference. */
import { Plus, Trash2 } from 'lucide-react';
import { LEDGER_PAYMENT_METHODS } from '../utils/posOrder';

const input = 'w-full px-3 py-3 text-base border border-input rounded-lg';

/**
 * @param {{
 *   lines: Array<{amount: number, method: string, reference: string}>,
 *   onChange: (lines: Array<{amount: number, method: string, reference: string}>) => void,
 * }} props
 */
const PosPaymentLinesInput = ({ lines, onChange }) => {
  const setLine = (index, patch) => onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const addLine = () => onChange([...lines, { amount: 0, method: 'cash', reference: '' }]);
  const removeLine = (index) => onChange(lines.filter((_, i) => i !== index));

  return (
    <div className="space-y-2">
      {lines.map((l, index) => (
        <div key={index} className="flex gap-2 items-start">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            placeholder="Amount"
            value={l.amount || ''}
            onChange={(e) => setLine(index, { amount: Number(e.target.value) || 0 })}
            className={input}
          />
          <select
            value={l.method}
            onChange={(e) => setLine(index, { method: e.target.value })}
            className={`${input} max-w-[9rem]`}
          >
            {LEDGER_PAYMENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <input
            placeholder="Reference (optional)"
            value={l.reference}
            onChange={(e) => setLine(index, { reference: e.target.value })}
            className={input}
          />
          <button
            type="button"
            onClick={() => removeLine(index)}
            disabled={lines.length <= 1}
            className="p-3 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30"
            aria-label="Remove payment"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addLine}
        className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
      >
        <Plus size={14} /> Add another payment
      </button>
    </div>
  );
};

export default PosPaymentLinesInput;
